import { CheckCircle2, XCircle, AlertTriangle, Download, Package, Boxes, Tags, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface ImportResultsData {
  products: {
    created: number;
    updated: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  };
  rawMaterials: {
    created: number;
    updated: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  };
  modifiers: {
    groupsCreated: number;
    optionsCreated: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  };
  categories: {
    created: number;
    updated: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  };
}

interface ImportResultsProps {
  results: ImportResultsData;
  onDownloadErrors: () => void;
  onClose: () => void;
}

export function ImportResults({ results, onDownloadErrors, onClose }: ImportResultsProps) {
  const totalCreated =
    results.products.created +
    results.rawMaterials.created +
    results.modifiers.groupsCreated +
    results.categories.created;

  const totalUpdated =
    results.products.updated +
    results.rawMaterials.updated +
    results.categories.updated;

  const totalFailed =
    results.products.failed +
    results.rawMaterials.failed +
    results.modifiers.failed +
    results.categories.failed;

  const allErrors = [
    ...results.products.errors.map(e => ({ ...e, type: 'Product' })),
    ...results.rawMaterials.errors.map(e => ({ ...e, type: 'Raw Material' })),
    ...results.modifiers.errors.map(e => ({ ...e, type: 'Modifier' })),
    ...results.categories.errors.map(e => ({ ...e, type: 'Category' })),
  ];

  const hasErrors = allErrors.length > 0;
  const isSuccess = totalFailed === 0;

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <div className={cn(
        'flex items-center gap-4 p-4 rounded-lg',
        isSuccess ? 'bg-green-500/10' : 'bg-yellow-500/10'
      )}>
        {isSuccess ? (
          <CheckCircle2 className="h-8 w-8 text-green-500" />
        ) : (
          <AlertTriangle className="h-8 w-8 text-yellow-500" />
        )}
        <div>
          <p className={cn('font-medium', isSuccess ? 'text-green-700' : 'text-yellow-700')}>
            {isSuccess ? 'Import completed successfully' : 'Import completed with some errors'}
          </p>
          <p className="text-sm text-muted-foreground">
            {totalCreated} created, {totalUpdated} updated
            {totalFailed > 0 && `, ${totalFailed} failed`}
          </p>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Products */}
        <div className="p-4 border rounded-lg space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Package className="h-4 w-4" />
            <span className="text-sm font-medium">Products</span>
          </div>
          <div className="space-y-1">
            {results.products.created > 0 && (
              <div className="flex justify-between text-sm">
                <span>Created</span>
                <span className="text-green-600">+{results.products.created}</span>
              </div>
            )}
            {results.products.updated > 0 && (
              <div className="flex justify-between text-sm">
                <span>Updated</span>
                <span className="text-blue-600">{results.products.updated}</span>
              </div>
            )}
            {results.products.failed > 0 && (
              <div className="flex justify-between text-sm">
                <span>Failed</span>
                <span className="text-destructive">{results.products.failed}</span>
              </div>
            )}
            {results.products.created === 0 && results.products.updated === 0 && results.products.failed === 0 && (
              <div className="text-sm text-muted-foreground">No products imported</div>
            )}
          </div>
        </div>

        {/* Raw Materials */}
        <div className="p-4 border rounded-lg space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Boxes className="h-4 w-4" />
            <span className="text-sm font-medium">Raw Materials</span>
          </div>
          <div className="space-y-1">
            {results.rawMaterials.created > 0 && (
              <div className="flex justify-between text-sm">
                <span>Created</span>
                <span className="text-green-600">+{results.rawMaterials.created}</span>
              </div>
            )}
            {results.rawMaterials.updated > 0 && (
              <div className="flex justify-between text-sm">
                <span>Updated</span>
                <span className="text-blue-600">{results.rawMaterials.updated}</span>
              </div>
            )}
            {results.rawMaterials.failed > 0 && (
              <div className="flex justify-between text-sm">
                <span>Failed</span>
                <span className="text-destructive">{results.rawMaterials.failed}</span>
              </div>
            )}
            {results.rawMaterials.created === 0 && results.rawMaterials.updated === 0 && results.rawMaterials.failed === 0 && (
              <div className="text-sm text-muted-foreground">No raw materials imported</div>
            )}
          </div>
        </div>

        {/* Modifiers */}
        <div className="p-4 border rounded-lg space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Tags className="h-4 w-4" />
            <span className="text-sm font-medium">Modifiers</span>
          </div>
          <div className="space-y-1">
            {results.modifiers.groupsCreated > 0 && (
              <div className="flex justify-between text-sm">
                <span>Groups</span>
                <span className="text-green-600">+{results.modifiers.groupsCreated}</span>
              </div>
            )}
            {results.modifiers.optionsCreated > 0 && (
              <div className="flex justify-between text-sm">
                <span>Options</span>
                <span className="text-green-600">+{results.modifiers.optionsCreated}</span>
              </div>
            )}
            {results.modifiers.failed > 0 && (
              <div className="flex justify-between text-sm">
                <span>Failed</span>
                <span className="text-destructive">{results.modifiers.failed}</span>
              </div>
            )}
            {results.modifiers.groupsCreated === 0 && results.modifiers.optionsCreated === 0 && results.modifiers.failed === 0 && (
              <div className="text-sm text-muted-foreground">No modifiers imported</div>
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="p-4 border rounded-lg space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FolderTree className="h-4 w-4" />
            <span className="text-sm font-medium">Categories</span>
          </div>
          <div className="space-y-1">
            {results.categories.created > 0 && (
              <div className="flex justify-between text-sm">
                <span>Created</span>
                <span className="text-green-600">+{results.categories.created}</span>
              </div>
            )}
            {results.categories.updated > 0 && (
              <div className="flex justify-between text-sm">
                <span>Updated</span>
                <span className="text-blue-600">{results.categories.updated}</span>
              </div>
            )}
            {results.categories.failed > 0 && (
              <div className="flex justify-between text-sm">
                <span>Failed</span>
                <span className="text-destructive">{results.categories.failed}</span>
              </div>
            )}
            {results.categories.created === 0 && results.categories.updated === 0 && results.categories.failed === 0 && (
              <div className="text-sm text-muted-foreground">No categories imported</div>
            )}
          </div>
        </div>
      </div>

      {/* Errors List */}
      {hasErrors && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-destructive">Errors ({allErrors.length})</h4>
            <Button variant="outline" size="sm" onClick={onDownloadErrors}>
              <Download className="h-4 w-4 mr-2" />
              Download Error Report
            </Button>
          </div>
          <ScrollArea className="h-[200px] border rounded-lg">
            <div className="p-3 space-y-2">
              {allErrors.map((error, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{error.type} (Row {error.row}):</span>{' '}
                    <span className="text-muted-foreground">{error.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Close Button */}
      <div className="flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}
